import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import ProfileMenu from './profile-menu';
import { useAppSelector } from '../app/hooks';
import { selectLoggedInUser } from '../features/auth/auth.slice';
import { PermissionCode, UserType } from '../shared/enums';
import { MenuItem, styled } from '@mui/material';
import theme from '../app/app-theme';
import logo from '../resources/images/logo.png';
import {
  AttendanceIcon,
  BusinessIcon,
  ChecklistIcon,
  CurrencyExchangeIcon,
  DashboardIcon,
  EventAvailableIcon,
  FitnessCenterIcon,
  MemberIcon,
  MembershipIcon,
  MenuIcon,
  MessageIcon,
  MoneyIcon,
  MonitorIcon,
  PlansIcon,
  PriceCheckIcon,
  RestaurantMenuIcon,
  ShoppingCartIcon,
  StoreIcon,
  UsersIcon,
  VerifiedUserIcon,
} from '../shared/icons';
import { getPermissionsForLoggedInUser } from '../shared/utils';
import { isMember } from '../helpers/user-helpers';

const drawerWidth = 240;

enum Section {
  MAIN = 'Main',
  MEMBER_MANAGEMENT = 'Member Management',
  STAFF_MANAGEMENT = 'Staff Management',
  STORE = 'Store',
  FINANCE = 'Finance',
}

enum SideBarItem {
  DASHBOARD,
  PLANS,
  MEMBERS,
  DEVICES,
  ATTENDANCE,
  BUSINESS,
  MEMBERSHIPS,
  MEMBER_PAYMENTS,
  BUSINESS_PAYMENTS,
  MESSAGES,
  BILLING,
  NOTES,
  USERS,
  STAFF_ATTENDANCE,
  PRODUCTS,
  SALES,
  MEMBER_NUTRITION,
  MEMBER_WORKOUT,
  PERMISSIONS,
}

enum SideBarItemUrl {
  DASHBOARD = '/dashboard',
  PLANS = '/plans',
  MEMBERS = '/members',
  ATTENDANCE = '/attendance',
  STAFF_ATTENDANCE = '/staff-attendance',
  BUSINESS = '/business',
  MEMBERSHIPS = '/memberships',
  MEMBER_PAYMENTS = '/member-payments',
  BUSINESS_PAYMENTS = '/business-payments',
  MESSAGES = '/messages',
  BILLING = '/billing',
  NOTES = '/notes',
  USERS = '/users',
  PRODUCTS = '/products',
  SALES = '/sales',
  MEMBER_NUTRITION = '/member/nutrition',
  MEMBER_WORKOUT = '/member/workout',
  PERMISSIONS = '/permissions',
}

type SidebarItemConfig = {
  id: SideBarItem;
  label: string;
  path: string;
  section: Section;
  icon: React.ReactNode;
  visible?: boolean;
};

const StyledMenu = styled(List)(({ theme }) => ({
  padding: 0,
  '& .MuiMenuItem-root': {
    paddingTop: '0.25rem',
    paddingBottom: '0.25rem',
    marginBottom: '0.25rem',
    marginTop: '0.25rem',
    borderRadius: '1rem',
  },
  '& .MuiListItemIcon-root': {
    marginRight: '20px',
  },
  '& .MuiMenuItem-root.Mui-selected': {
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: theme.palette.primary.contrastText,
    '& .MuiListItemIcon-root, & .MuiSvgIcon-root, & .MuiListItemText-root': {
      color: theme.palette.primary.contrastText,
    },
    '&:hover': {
      backgroundColor: `${theme.palette.primary.main} !important`,
      color: theme.palette.primary.contrastText,
    },
  },
}));

const drawerPaperStyles = {
  boxSizing: 'border-box' as const,
  width: drawerWidth,
  backgroundColor: theme.palette.primary.contrastText,
  color: theme.palette.primary.main,
  '& .MuiSvgIcon-root': {
    color: theme.palette.primary.main,
  },
};

const getSidebarItemFromPath = (pathname: string): SideBarItem => {
  switch (pathname) {
    case SideBarItemUrl.DASHBOARD:
      return SideBarItem.DASHBOARD;
    case SideBarItemUrl.PLANS:
      return SideBarItem.PLANS;
    case SideBarItemUrl.MEMBERS:
      return SideBarItem.MEMBERS;
    case SideBarItemUrl.ATTENDANCE:
      return SideBarItem.ATTENDANCE;
    case SideBarItemUrl.MESSAGES:
      return SideBarItem.MESSAGES;
    case SideBarItemUrl.STAFF_ATTENDANCE:
      return SideBarItem.STAFF_ATTENDANCE;
    case SideBarItemUrl.BUSINESS:
      return SideBarItem.BUSINESS;
    case SideBarItemUrl.MEMBERSHIPS:
      return SideBarItem.MEMBERSHIPS;
    case SideBarItemUrl.MEMBER_PAYMENTS:
      return SideBarItem.MEMBER_PAYMENTS;
    case SideBarItemUrl.BUSINESS_PAYMENTS:
      return SideBarItem.BUSINESS_PAYMENTS;
    case SideBarItemUrl.BILLING:
      return SideBarItem.BILLING;
    case SideBarItemUrl.NOTES:
      return SideBarItem.NOTES;
    case SideBarItemUrl.USERS:
      return SideBarItem.USERS;
    case SideBarItemUrl.PRODUCTS:
      return SideBarItem.PRODUCTS;
    case SideBarItemUrl.SALES:
      return SideBarItem.SALES;
    case SideBarItemUrl.MEMBER_NUTRITION:
      return SideBarItem.MEMBER_NUTRITION;
    case SideBarItemUrl.MEMBER_WORKOUT:
      return SideBarItem.MEMBER_WORKOUT;
    case SideBarItemUrl.PERMISSIONS:
      return SideBarItem.PERMISSIONS;
    default:
      return SideBarItem.DASHBOARD;
  }
};

const getSidebarItems = (loggedInUser: any, permissions: PermissionCode[]): SidebarItemConfig[] => {
  const isGyminify = loggedInUser?.type === UserType.GYMINIFY;
  const isMember = loggedInUser?.type === UserType.MEMBER;

  const items: SidebarItemConfig[] = [
    { id: SideBarItem.DASHBOARD, label: 'Dashboard', path: '/dashboard', section: Section.MAIN, icon: <DashboardIcon /> },
    { id: SideBarItem.PLANS, label: 'Plans', path: '/plans', section: Section.MAIN, icon: <PlansIcon />, visible: isGyminify },
    { id: SideBarItem.BUSINESS, label: 'Business', path: '/business', section: Section.MAIN, icon: <BusinessIcon />, visible: isGyminify },
    { id: SideBarItem.DEVICES, label: 'Devices', path: '/devices', section: Section.MAIN, icon: <MonitorIcon />, visible: permissions.includes(PermissionCode.BIOMETRIC_DEVICE_VIEW) },
    { id: SideBarItem.BUSINESS_PAYMENTS, label: 'Income', path: '/business-payments', section: Section.FINANCE, icon: <MoneyIcon />, visible: isGyminify },
    { id: SideBarItem.MEMBERS, label: 'Members', path: '/members', section: Section.MEMBER_MANAGEMENT, icon: <MemberIcon />, visible: permissions.includes(PermissionCode.MEMBERS_VIEW) },
    { id: SideBarItem.ATTENDANCE, label: 'Attendance', path: '/attendance', section: Section.MEMBER_MANAGEMENT, icon: <AttendanceIcon />, visible: permissions.includes(PermissionCode.MEMBER_ATTENDANCE_VIEW) || isMember },
    { id: SideBarItem.NOTES, label: 'Notes', path: '/notes', section: Section.MAIN, icon: <ChecklistIcon />, visible: permissions.includes(PermissionCode.NOTES_VIEW) },
    { id: SideBarItem.PRODUCTS, label: 'Products', path: '/products', section: Section.STORE, icon: <StoreIcon />, visible: permissions.includes(PermissionCode.PRODUCTS_VIEW) },
    { id: SideBarItem.SALES, label: 'Sales', path: '/sales', section: Section.STORE, icon: <ShoppingCartIcon />, visible: permissions.includes(PermissionCode.SALES_VIEW) },
    { id: SideBarItem.MEMBERSHIPS, label: 'Memberships', path: '/memberships', section: Section.MEMBER_MANAGEMENT, icon: <MembershipIcon />, visible: permissions.includes(PermissionCode.MEMBERSHIPS_VIEW) },
    { id: SideBarItem.MEMBER_PAYMENTS, label: isMember ? 'Payments' : 'Income', path: '/member-payments', section: Section.FINANCE, icon: <PriceCheckIcon />, visible: permissions.includes(PermissionCode.INCOME_VIEW) || isMember },
    { id: SideBarItem.BILLING, label: 'Billing', path: '/billing', section: Section.FINANCE, icon: <CurrencyExchangeIcon />, visible: permissions.includes(PermissionCode.BILLING_VIEW) },
    { id: SideBarItem.MESSAGES, label: 'Messages', path: '/messages', section: Section.MAIN, icon: <MessageIcon />, visible: isGyminify || isMember || permissions.includes(PermissionCode.MESSAGES_VIEW) },
    { id: SideBarItem.USERS, label: isGyminify ? 'Users' : 'Staff', path: '/users', section: Section.STAFF_MANAGEMENT, icon: <UsersIcon />, visible: isGyminify || permissions.includes(PermissionCode.STAFF_VIEW) },
    { id: SideBarItem.STAFF_ATTENDANCE, label: 'Attendance', path: '/staff-attendance', section: Section.STAFF_MANAGEMENT, icon: <EventAvailableIcon />, visible: permissions.includes(PermissionCode.STAFF_ATTENDANCE_VIEW) },
    { id: SideBarItem.PERMISSIONS, label: 'Permissions', path: '/permissions', section: Section.STAFF_MANAGEMENT, icon: <VerifiedUserIcon />, visible: permissions.includes(PermissionCode.ROLES_VIEW) },
    { id: SideBarItem.MEMBER_NUTRITION, label: 'Nutrition', path: '/member/nutrition', section: Section.MEMBER_MANAGEMENT, icon: <RestaurantMenuIcon />, visible: isMember },
    { id: SideBarItem.MEMBER_WORKOUT, label: 'Workout', path: '/member/workout', section: Section.MEMBER_MANAGEMENT, icon: <FitnessCenterIcon />, visible: isMember },
  ];

  return items.filter((item) => item.visible ?? true);
};

export default function HomeNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const loggedInUser = useAppSelector(selectLoggedInUser);
  const permissions = getPermissionsForLoggedInUser(loggedInUser);

  const [selectedItem, setSelectedItem] = React.useState<SideBarItem>(getSidebarItemFromPath(location.pathname));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const sidebarItems = React.useMemo(() => getSidebarItems(loggedInUser, permissions), [loggedInUser, permissions]);
  const groupedSidebarItems = React.useMemo(
    () =>
      Object.values(Section)
        .map((section) => ({
          section,
          items: sidebarItems.filter((item) => item.section === section),
        }))
        .filter(({ items }) => items.length > 0),
    [sidebarItems]
  );

  React.useEffect(() => {
    setSelectedItem(getSidebarItemFromPath(location.pathname));
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const onSelectItem = (item: SideBarItem, url: string) => {
    setSelectedItem(item);
    navigate(url);
  };

  const drawer = (
    <div>
      <Toolbar />
      <Divider />
      <StyledMenu sx={{ paddingTop: 0, paddingLeft: 1, paddingRight: 1 }}>
        {groupedSidebarItems.map(({ section, items }) => (
          <React.Fragment key={section}>
            <Typography
              variant="overline"
              sx={{
                display: (section === Section.MAIN || isMember(loggedInUser)) ? 'none' : 'block',
                px: 2,
                pt: 1,
                pb: 0,
                color: 'text.secondary',
                fontWeight: 700,
                letterSpacing: '0.08em',
              }}
            >
              {section}
            </Typography>
            {items.map((item) => {
              const isSelected = selectedItem === item.id;

              return (
                <MenuItem
                  key={item.id}
                  onClick={() => onSelectItem(item.id, item.path)}
                  sx={{
                    cursor: 'pointer',
                    '& .Mui-selected': {
                      backgroundColor: `${theme.palette.primary.main} !important`,
                      color: theme.palette.primary.contrastText,
                      '& .MuiListItemIcon-root, & .MuiSvgIcon-root, & .MuiListItemText-root': {
                        color: theme.palette.primary.contrastText,
                      },
                      '&:hover': {
                        backgroundColor: `${theme.palette.primary.main} !important`,
                        color: theme.palette.primary.contrastText,
                      },
                    },
                  }}
                  selected={isSelected}
                  dense
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </MenuItem>
              );
            })}
          </React.Fragment>
        ))}
      </StyledMenu>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, backgroundColor: 'white' }} elevation={0}>
        <Toolbar>
          <IconButton
            color="default"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <img src={logo} style={{ maxWidth: '24px' }} alt="" />
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, color: '#641197' }}>
            yminify
          </Typography>
          <ProfileMenu />
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClick={handleDrawerToggle}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': drawerPaperStyles,
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': drawerPaperStyles,
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` }, backgroundColor: (theme) => theme.palette.background.default }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
}
